import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Cog6ToothIcon, EnvelopeIcon } from '@heroicons/react/20/solid';
import { StarIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import { ISavedFilter, SavedFilterType } from 'shared';

import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useFetchSavedFiltersQuery } from '../../services/api/savedFiltersApi/savedFilters';
import { classNames } from '../../../common/lib/classNames';

import { SavedFilterSettingsModal } from './SavedFilterSettingsModal';
import { describeSchedule } from './SavedFilterScheduleFields';

// The sidebar only lists org-scoped saved filters; user-scoped report types
// live on the report pages themselves
export type OrgSavedFilterType = SavedFilterType.Contact | SavedFilterType.Invoice | SavedFilterType.Escalation | SavedFilterType.Action;

interface SavedFiltersNavProps {
  type?: OrgSavedFilterType;
  onNavigate?: () => void;
  theme?: `dark` | `light`;
  /** `inline` renders indented under a nav item, `nested` renders as a section of the nested sidebar */
  variant?: `inline` | `nested`;
}

// Where each saved filter type's table lives (relative to the org root)
const TYPE_PATHS: Record<OrgSavedFilterType, string> = {
  [SavedFilterType.Contact]: `contacts/manage`,
  [SavedFilterType.Invoice]: `invoices`,
  [SavedFilterType.Escalation]: `escalations`,
  [SavedFilterType.Action]: `actions`,
};

const THEME_CLASSES = {
  dark: {
    heading: `text-paidniceGreen-200`,
    row: `text-paidniceGreen-100 hover:text-white hover:bg-paidniceGreen-600`,
  },
  light: {
    heading: `text-gray-400`,
    row: `text-gray-600 hover:text-gray-900 hover:bg-gray-100`,
  },
};

// Lists the org's saved filters for a table in the left nav. Each item is
// just a shortcut into that table with ?savedFilterId= — the grid itself owns
// applying the saved MUI grid state.
export function SavedFiltersNav({
  type = SavedFilterType.Contact,
  onNavigate,
  theme = `dark`,
  variant = `inline`,
}: SavedFiltersNavProps) {
  const themeClasses = THEME_CLASSES[theme];
  const currentOrg = useGetSelectedOrganisation();
  const navigate = useNavigate();

  const { data } = useFetchSavedFiltersQuery(
    { organisationId: currentOrg?.id ?? ``, type },
    { skip: !currentOrg?.id },
  );
  const [settingsFilter, setSettingsFilter] = useState<ISavedFilter | null>(null);

  const savedFilters = data?.savedFilters ?? [];

  if (savedFilters.length === 0) return null;

  function applyFilter(id: string) {
    navigate(`/${currentOrg?.id}/${TYPE_PATHS[type]}?savedFilterId=${id}`);
    onNavigate?.();
  }

  function openSettings(e: React.MouseEvent, filter: ISavedFilter) {
    e.stopPropagation();
    setSettingsFilter(filter);
  }

  const rows = savedFilters.map(filter => (
    <div
      key={ filter.id }
      className={ classNames(
        `group flex items-center justify-between px-2 rounded ${themeClasses.row}`,
        variant === `nested` ? `py-1.5 text-sm rounded-md` : `py-1 text-xs`,
      ) }
    >
      <button
        onClick={ () => applyFilter(filter.id) }
        className={ `flex items-center flex-1 min-w-0 text-left` }
        title={ filter.name }
      >
        <StarIcon className={ classNames(
          `h-3.5 w-3.5 shrink-0 mr-1.5`,
          variant === `inline` && `-ml-5`,
        ) } />
        <span className={ `truncate` }>{ filter.name }</span>
      </button>
      { filter.schedule && (
        <Tooltip title={ `Emailed ${describeSchedule(filter.schedule)} to ${filter.schedule.userIds.length} recipient${filter.schedule.userIds.length === 1 ? `` : `s`}` }>
          <EnvelopeIcon className={ `h-3.5 w-3.5 shrink-0 ml-1 opacity-60` } />
        </Tooltip>
      ) }
      <button
        onClick={ e => openSettings(e, filter) }
        className={ `ml-1 opacity-0 group-hover:opacity-100` }
        title={ `Saved filter settings` }
      >
        <Cog6ToothIcon className={ `h-4 w-4` } />
      </button>
    </div>
  ));

  const settingsModal = (
    <SavedFilterSettingsModal
      filter={ settingsFilter }
      organisationId={ currentOrg?.id ?? `` }
      onClose={ () => setSettingsFilter(null) }
    />
  );

  if (variant === `nested`) {
    return (
      <div className={ `mt-6 px-2` }>
        <p className={ `px-2 text-xs font-semibold uppercase tracking-wider ${themeClasses.heading}` }>
          { `Saved Filters` }
        </p>
        <div className={ `mt-1 space-y-1` }>
          { rows }
        </div>
        { settingsModal }
      </div>
    );
  }

  return (
    <div className={ `ml-10 mt-2 space-y-1` }>
      { rows }
      { settingsModal }
    </div>
  );
}
